import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { notification } from 'antd'
import ViewOverride from './ViewOverride'
import { useFeeMgmtNavigation } from '../hooks/useFeeMgmtNavigation'
import { useGridData } from '../context/GridDataContext'
import { mapApiDataToGridData } from '../constants/DataMapping'
import {
  fetchOverrideSheets,
  fetchOverrideData,
  updateOverrideSheet,
} from '../../../store/reducers/feeManagement/feeManagementSlice'
import {
  selectOverrideSheetById,
  selectOverrideSheetsLoading,
  selectOverrideSheetsError,
  selectOverrideDataBySheetId,
  selectOverrideDataLoading,
  selectOverrideDataError,
} from '../../../store/reducers/feeManagement/feeManagementSelectors'

const ViewOverrideContainer = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const { handleBackFromOverride } = useFeeMgmtNavigation()
  const [status, setStatus] = useState('inactive')

  // Get grid data context but don't actively fetch mapping data here
  const { isLoadingMappingData, mappingData } = useGridData()

  const sheet = useSelector((state) => selectOverrideSheetById(state, id))
  const overrides = useSelector((state) => selectOverrideDataBySheetId(state, id))
  const isLoadingSheet = useSelector(selectOverrideSheetsLoading)
  const isLoadingData = useSelector(selectOverrideDataLoading)
  const sheetError = useSelector(selectOverrideSheetsError)
  const dataError = useSelector(selectOverrideDataError)

  // Transform grid data with mapped names
  const gridData = useMemo(() => {
    if (!overrides) {
      return null
    }

    // Map the API data to grid format (now includes product and vendor name mapping)
    return mapApiDataToGridData(overrides, mappingData)
  }, [overrides, mappingData])

  // Set initial status from override data
  useEffect(() => {
    if (sheet?.status) {
      setStatus(sheet.status)
    }
  }, [sheet])

  const handleSave = useCallback(
    async (result) => {
      // The save path diffs against the loaded sheet and skips the request when
      // nothing actually changed — no refetch needed either.
      if (result?.noChanges) {
        notification.info({
          message: 'No changes to save',
          description: 'The sheet already matches what is on the server.',
        })
        return
      }
      // GridContainer has already persisted the edits via the bulk PUT and only
      // invokes onSave on success. Refetch server truth so the grid reflects
      // server-assigned ids for new rows and drops soft-deleted rows.
      const inserted = result?.insertedCount || result?.inserted_count || 0
      const updated = result?.updatedCount || result?.updated_count || 0
      notification.success({
        message: 'Fees updated',
        description: `Saved ${updated} update${updated === 1 ? '' : 's'} and ${inserted} new row${
          inserted === 1 ? '' : 's'
        }.`,
      })
      await dispatch(fetchOverrideData(id))
    },
    [dispatch, id],
  )

  const handleStatusChange = useCallback(
    (checked) => {
      const newStatus = checked ? 'active' : 'inactive'
      setStatus(newStatus)
      dispatch(updateOverrideSheet({ id: sheet.id, active: checked })).catch(() => {
        setStatus(checked ? 'active' : 'inactive')
      })
    },
    [sheet, dispatch],
  )

  // Transform override data for the view component
  const transformedOverride = useMemo(() => {
    if (!sheet) {
      return null
    }
    return {
      id: sheet.id,
      title: sheet.title,
      overrideType: sheet.overrideType,
      overriddenFields: sheet.overriddenFields,
      details: {
        'Total Overrides': gridData?.length || 0,
        'Created By': sheet.created_by,
        'Created Date': sheet.created,
        'Last Modified': sheet.created,
      },
    }
  }, [sheet, gridData?.length])

  // Fetch data
  useEffect(() => {
    if (!sheet && !sheetError) {
      dispatch(fetchOverrideSheets())
    }
    dispatch(fetchOverrideData(id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const buildErrorString = () => {
    if (!sheetError && !dataError) {
      return ''
    }
    if (sheetError && dataError) {
      return `${sheetError} and ${dataError}`
    }
    return sheetError || dataError
  }

  const error = buildErrorString()

  return (
    <ViewOverride
      override={transformedOverride}
      gridData={gridData}
      isLoading={isLoadingSheet || isLoadingData || isLoadingMappingData}
      onBack={() => handleBackFromOverride(sheet?.overrideType)}
      onStatusChange={handleStatusChange}
      onSave={handleSave}
      status={status}
      error={error}
    />
  )
}

export default ViewOverrideContainer
